import { Injectable } from '@angular/core';

export interface UrlThemeConfig {
  primaryColor: string;
  primaryRgb: string;
  borderRadius: string;
  borderRadiusPanel: string;
  fontFamily: string;
  ocultarLinksAjuda?: boolean;
}

const HOSTNAME_INTERO = 'intero.alterdata.com.br';

const URL_THEMES: Record<string, UrlThemeConfig> = {
  [HOSTNAME_INTERO]: {
    primaryColor: '#130f40',
    primaryRgb: '19, 15, 64',
    borderRadius: '50rem',
    borderRadiusPanel: '1.5rem',
    fontFamily: "'Roboto', sans-serif",
    ocultarLinksAjuda: true,
  },
  'novo2.alterdata.com.br': {
    primaryColor: '#c0392b',
    primaryRgb: '192, 57, 43',
    borderRadius: '50rem',
    borderRadiusPanel: '2rem',
    fontFamily: "'Roboto', sans-serif",
  },
  // Adicione novos temas aqui:
  // 'outro.dominio.com.br': {
  //   primaryColor: '#...',
  //   primaryRgb: '...',
  //   borderRadiusPanel: '...',
  //   borderRadius: '...',
  //   fontFamily: '...',
  //   ocultarLinksAjuda: true,
  // },
};

const DEFAULT_THEME: UrlThemeConfig = {
  primaryColor: 'rgba(0, 96, 156, 1)',
  primaryRgb: '0, 96, 156',
  borderRadius: '0.4rem',
  borderRadiusPanel: '0.4rem',
  fontFamily: 'inherit',
};

// Chave usada apenas em DSN para persistir a preferência de tema Íntero entre reloads
const CHAVE_TEMA_INTERO_DSN = 'tema-intero-dsn';

// Tema Smart — herda geometria do Intero, muda apenas a cor primária para laranja
const SMART_THEME: UrlThemeConfig = {
  primaryColor: '#ff6100',
  primaryRgb: '255, 97, 0',
  borderRadius: '50rem',
  borderRadiusPanel: '1.5rem',
  fontFamily: "'Roboto', sans-serif",
  ocultarLinksAjuda: true,
};

// Chave localStorage para persistir tema Smart entre reloads
const CHAVE_TEMA_SMART = 'tema-smart';

@Injectable({ providedIn: 'root' })
export class UrlThemeService {

  get ocultarLinksAjuda(): boolean {
    return this.temaSmartAtivo
      || this.temaInteroAtivo
      || (URL_THEMES[window.location.hostname]?.ocultarLinksAjuda ?? false);
  }

  get temaSmartAtivo(): boolean {
    return document.documentElement.classList.contains('tema-smart');
  }

  get temaInteroAtivo(): boolean {
    return document.documentElement.classList.contains('tema-intero');
  }

  // Limpa AMBOS os temas por pacote (chamado no logout)
  limparTemasPorPacote(): void {
    localStorage.removeItem(CHAVE_TEMA_SMART);
    localStorage.removeItem(CHAVE_TEMA_INTERO_DSN);
  }

  // Disponível apenas em DSN. Salva/remove a preferência e reaplicar o tema.
  // O reload após esta chamada garante que todos os componentes reinicializam corretamente.
  alternarTemaDsn(): void {
    const deveAtivar = !this.temaInteroAtivo;
    deveAtivar
      ? localStorage.setItem(CHAVE_TEMA_INTERO_DSN, 'true')
      : localStorage.removeItem(CHAVE_TEMA_INTERO_DSN);
  }

  // Ponto único de aplicação do tema no DOM.
  // Chamado no boot da aplicação e após desativar o tema DSN.
  apply(): void {
    const hostname = window.location.hostname;
    const root = document.documentElement;

    // 1. Prioridade máxima: hostname Intero
    const temaInteroAtivoPorHostname = hostname.startsWith(HOSTNAME_INTERO);

    // 2. Segunda prioridade: tema Smart por pacote (flag explícita OU cache de pacotes)
    const temaSmartAtivo = localStorage.getItem(CHAVE_TEMA_SMART) === 'true'
      || this.temPacoteSmartNoCache();

    // 3. Terceira prioridade: tema Intero por pacote (DSN toggle)
    const temaInteroAtivoPorDsn = localStorage.getItem(CHAVE_TEMA_INTERO_DSN) === 'true';

    // Resolver config na ordem de prioridade
    let config: UrlThemeConfig;
    let temaAtualKey: string;

    if (temaInteroAtivoPorHostname) {
      config = URL_THEMES[HOSTNAME_INTERO];
      temaAtualKey = HOSTNAME_INTERO;
    } else if (temaSmartAtivo) {
      config = SMART_THEME;
      temaAtualKey = 'smart';
    } else if (temaInteroAtivoPorDsn) {
      config = URL_THEMES[HOSTNAME_INTERO];
      temaAtualKey = HOSTNAME_INTERO;
    } else {
      config = URL_THEMES[hostname] ?? DEFAULT_THEME;
      temaAtualKey = URL_THEMES[hostname] ? hostname : 'default';
    }

    // Aplicar custom properties
    root.style.setProperty('--theme-primary', config.primaryColor);
    root.style.setProperty('--theme-primary-rgb', config.primaryRgb);
    root.style.setProperty('--theme-border-radius', config.borderRadius);
    root.style.setProperty('--theme-border-radius-panel', config.borderRadiusPanel);
    root.style.setProperty('--theme-font-family', config.fontFamily);

    root.setAttribute('data-url-theme', temaAtualKey);
    root.setAttribute('data-ocultar-links-ajuda', String(config.ocultarLinksAjuda ?? false));

    // Classes no <html>
    // Smart TAMBÉM adiciona 'tema-intero' porque os overrides de input-group pill
    // no url-theme.scss dependem dessa classe. 'tema-smart' é adicionada extra para
    // permitir customizações específicas (ex: textos condicionais no login).
    if (temaInteroAtivoPorHostname || temaInteroAtivoPorDsn) {
      root.classList.add('tema-intero');
      root.classList.remove('tema-smart');
    } else if (temaSmartAtivo) {
      root.classList.add('tema-intero');  // Herda overrides visuais do Intero
      root.classList.add('tema-smart');   // Permite customizações Smart-only
    } else {
      root.classList.remove('tema-intero');
      root.classList.remove('tema-smart');
    }

    this.aplicarFavicon(temaSmartAtivo);
  }

  private aplicarFavicon(isSmart: boolean): void {
    const link = document.getElementById('favicon') as HTMLLinkElement | null;
    if (!link) return;

    if (isSmart) {
      link.href = 'assets/favicons/erp4meSmart/favicon.ico';
    }
    // Outros temas: não alterar o favicon
  }

  private temPacoteSmartNoCache(): boolean {
    try {
      const raw = localStorage.getItem('pacote');
      if (!raw) return false;
      const pacotes: { identificadorProduto: string }[] = JSON.parse(raw);
      return pacotes.some(p => p.identificadorProduto === '0010003GVV');
    } catch {
      return false;
    }
  }
}
